/**
 * Phase 7 · inline `.qc` block handler.
 * Ports `qcReveal()` and option-select pattern from `01_BUILD/qa_studio.html`
 * L22597 + L1178 styles.
 *
 * Per Genie ack 2 MAY 2026 (#4=α coexist) · this is the additive content-author
 * tool · DOES NOT replace the structured `<QuickCheck />` React component.
 *
 * Markup contract:
 *   <div class="qc" data-qc-id="<unique>" data-correct="<option-id>">
 *     <div class="qc-question">…?</div>
 *     <div class="qc-options">
 *       <label><input type="radio" name="qc-X" value="a"/> Option A</label>
 *       <label><input type="radio" name="qc-X" value="b"/> Option B</label>
 *     </div>
 *     <details class="qc-explain"><summary>Why?</summary>Explanation…</details>
 *   </div>
 */

import type { InlineQcState } from '@/types';

interface InlineQcSetState {
  (blockId: string, state: InlineQcState): void;
}

export function initInlineQc(root: HTMLElement, setState: InlineQcSetState): () => void {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>('.qc'));
  const cleanups: Array<() => void> = [];

  for (const block of blocks) {
    if (block.dataset.qcInit === '1') continue;
    block.dataset.qcInit = '1';

    const blockId = block.dataset.qcId ?? `qc-${Math.random().toString(36).slice(2, 8)}`;
    if (!block.dataset.qcId) block.dataset.qcId = blockId;
    const correctId = block.dataset.correct ?? '';
    const optionsContainer = block.querySelector<HTMLElement>('.qc-options');
    const explain = block.querySelector<HTMLElement>('.qc-explain');
    if (!optionsContainer) continue;

    const labels = Array.from(optionsContainer.querySelectorAll<HTMLLabelElement>('label'));

    const onChange = (e: Event) => {
      if (optionsContainer.classList.contains('answered')) return;
      const input = e.target as HTMLInputElement;
      if (input.tagName !== 'INPUT' || input.type !== 'radio') return;
      const selected = input.value;
      const isCorrect = selected === correctId;
      optionsContainer.classList.add('answered');
      labels.forEach((lbl) => {
        const lblInput = lbl.querySelector<HTMLInputElement>('input[type="radio"]');
        const v = lblInput?.value;
        lbl.classList.toggle('selected', v === selected);
        lbl.classList.toggle('correct', v === correctId);
        if (v === selected && !isCorrect) lbl.classList.add('wrong');
      });
      if (explain) explain.toggleAttribute('open', !isCorrect);
      setState(blockId, {
        state: isCorrect ? 'correct' : 'wrong',
        selectedOption: selected,
        revealedAt: new Date().toISOString(),
      });
    };

    optionsContainer.addEventListener('change', onChange);
    cleanups.push(() => {
      optionsContainer.removeEventListener('change', onChange);
      delete block.dataset.qcInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}

/**
 * Re-apply persisted inline QC state to DOM after re-render or first paint.
 */
export function syncInlineQcDom(
  root: HTMLElement,
  inlineQcState: Record<string, InlineQcState>,
): void {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>('.qc'));
  for (const block of blocks) {
    const blockId = block.dataset.qcId;
    if (!blockId) continue;
    const persisted = inlineQcState[blockId];
    if (!persisted || persisted.state === 'pristine') continue;

    const correctId = block.dataset.correct ?? '';
    const optionsContainer = block.querySelector<HTMLElement>('.qc-options');
    const explain = block.querySelector<HTMLElement>('.qc-explain');
    if (!optionsContainer) continue;

    optionsContainer.classList.add('answered');
    const labels = Array.from(optionsContainer.querySelectorAll<HTMLLabelElement>('label'));
    labels.forEach((lbl) => {
      const input = lbl.querySelector<HTMLInputElement>('input[type="radio"]');
      const v = input?.value;
      const isSelected = v === persisted.selectedOption;
      const isCorrect = v === correctId;
      lbl.classList.toggle('selected', isSelected);
      lbl.classList.toggle('correct', isCorrect);
      lbl.classList.toggle('wrong', isSelected && !isCorrect);
      if (input && isSelected) input.checked = true;
    });
    if (explain) explain.toggleAttribute('open', persisted.state === 'wrong');
  }
}
